import styled from "@emotion/styled";
import { notEmpty } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";
import { useMeasure } from "react-use";

import type { ContentSchemaRenderProps } from "~/components/schema-render/content";
import { normalizeJsxAttribute } from "~/lib/utils";
import type { imageContent$key } from "~/queries/__generated__/imageContent.graphql";

import { DynamicStarlingImagePopover } from "./dynamics";
import { ImageStyle } from "./enums";
import { checkContentContainsStarlingImage } from "./starling-image-popover/helpers/checkers";
import { Container, StyledArticleImage } from "./styles";

// TODO: missing swipe widget
export type ExtraProps = {
  enableResize?: boolean;
  lazyload?: boolean;
};

export type Props = {
  className?: string;
  enableResize?: boolean;
  height?: string;
  lazyload?: boolean;
  reference?: imageContent$key;
  src?: string;
  title?: string;
  width?: string;
} & ExtraProps &
  ContentSchemaRenderProps;

const Component: FunctionComponent<Props> = ({
  enableResize,
  height,
  lazyload = true,
  reference: reference_,
  schemaNode,
  src: source = "",
  title,
  width,
  ...attribs
}) => {
  const content = useFragment(
    graphql`
      fragment imageContent on Content {
        headline
        ...checkersCheckContentContainsStarlingImageContent
      }
    `,
    reference_,
  );

  const isFullWidth = width === "100%" || width === "100vw";
  const isHeightOrWidthEmpty = !notEmpty(height) || !notEmpty(width);
  const isPortrait = Number.parseInt(height ?? "0", 10) > Number.parseInt(width ?? "0", 10);

  let imageStyle = ImageStyle.Other;
  if (isFullWidth || isHeightOrWidthEmpty) imageStyle = ImageStyle.Full;
  if (isPortrait) imageStyle = ImageStyle.Fixed;

  const [reference, measurements] = useMeasure<HTMLDivElement>();

  const { top } = measurements ?? {};

  return (
    <Container>
      {checkContentContainsStarlingImage(content) && (
        <DynamicStarlingImagePopover source={source} />
      )}
      <StyledArticleImage
        $imageHeight={height}
        $imageStyle={imageStyle}
        $imageWidth={width}
        $relativeHeight={enableResize ? top : undefined}
        {...normalizeJsxAttribute(schemaNode.attribs)}
        {...attribs}
        data-headline={content?.headline}
        imageProps={{ $aspectRatio: "2/1", lazyload, src: source, title }}
        ref={reference}
        title={title ?? ""}
      />
    </Container>
  );
};
Component.displayName = "Image";
export const Image = styled(Component)``;
